/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

import { dagRepository } from '@/services/repositories/dag.repository.js'

/**
 * DAG Seeder
 * Creates dummy DAG check data for goats
 */

/**
 * Generate a random DAG score (1-5)
 * Higher probability for healthy scores (1-2)
 * @returns {number} Score between 1 and 5
 */
function randomDagScore() {
  const rand = Math.random()
  
  // 50% chance of score 1-2 (healthy)
  if (rand < 0.5) {
    return Math.random() < 0.6 ? 1 : 2
  }
  // 30% chance of score 3 (borderline)
  else if (rand < 0.8) {
    return 3
  }
  // 15% chance of score 4 (concerning)
  else if (rand < 0.95) {
    return 4
  }
  // 5% chance of score 5 (severe)
  else {
    return 5
  }
}

/**
 * Generate a random date in the past (last 90 days)
 * @returns {string} ISO date string
 */
function randomPastDate() {
  const now = Date.now()
  const daysAgo = Math.floor(Math.random() * 90) // 0-90 days ago
  const timestamp = now - (daysAgo * 24 * 60 * 60 * 1000)
  return new Date(timestamp).toISOString()
}

/**
 * Seed DAG checks for a single goat
 * @param {number} goatId
 * @param {number} count - number of DAG records to create
 * @returns {Promise<number[]>} array of created DAG check IDs
 */
async function seedDagForGoat(goatId, count = 3) {
  console.log(`  Seeding ${count} DAG checks for goat ${goatId}...`)
  const createdIds = []

  for (let i = 0; i < count; i++) {
    const id = await dagRepository.create({
      goatId,
      score: randomDagScore(),
      measuredAt: randomPastDate()
    })
    createdIds.push(id)
    console.log(
      `    Created DAG check: Score ${id.score} (id: ${id.id})`
    )
  }

  return createdIds
}

/**
 * Seed DAG checks for a list of goats
 * @param {Array<{id: number}>} goats - array of goat objects with id
 * @param {number} checksPerGoat - number of DAG records per goat
 * @returns {Promise<void>}
 */
export async function seedDag(goats = [], checksPerGoat = 3) {
  if (goats.length === 0) {
    console.log(' No goats provided for DAG seeding.')
    return
  }

  console.log(`Seeding DAG checks for ${goats.length} goats...`)

  try {
    for (const goat of goats) {
      await seedDagForGoat(goat.id, checksPerGoat)
    }

    console.log(`Seeded DAG checks for ${goats.length} goats`)
  } catch (error) {
    console.error('Failed to seed DAG checks:', error)
    throw error
  }
}
